/**
 * CommunicationPatternsTable — side-by-side analytical comparison of the
 * *form* of current hantavirus official statements against publicly-documented
 * COVID-era institutional communication patterns.
 *
 * IMPORTANT: This component carries a non-dismissible banner that enforces the
 * analytical framing — NOT a threat-equivalence claim.
 *
 * Design rules (uncodixify-ui.md):
 *   - No hover lift, no shadow escalation, no animate-pulse on badges.
 *   - Severity badges use semantic colour tokens with explicit dark variants.
 *   - All directional spacing via ms-* / me-*; external-link icon mirrors via
 *     rtl:scale-x-[-1].
 *   - Touch targets ≥ 44×44 px via parent container.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - No physical left/right; use start-* / end-* and text-start/text-end.
 *   - No rtl:flex-row-reverse — the app sets dir="rtl" on <html> for Arabic.
 *
 * Prompt #08 (communication-patterns-covid-comparison).
 */

import { ExternalLink } from "lucide-react";
import { useCommunicationPatterns } from "#/hooks/useCommunicationPatterns.js";
import type { CommunicationPatternItem } from "#/hooks/useCommunicationPatterns.js";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CommunicationPatternsTableProps {
  locale: string;
  t: TranslateFn;
}

// ---------------------------------------------------------------------------
// Helper: Severity badge
// ---------------------------------------------------------------------------

function SeverityBadge({
  severity,
  t,
}: {
  severity: "HIGH" | "MEDIUM";
  t: TranslateFn;
}) {
  const label =
    severity === "HIGH" ? t("communication:severity.high") : t("communication:severity.medium");

  const colourClass =
    severity === "HIGH"
      ? "bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800"
      : "bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${colourClass}`}
      role="status"
      aria-label={`${t("communication:columns.severity")}: ${label}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helper: Match badge
// ---------------------------------------------------------------------------

function MatchBadge({ match, t }: { match: boolean; t: TranslateFn }) {
  const label = match ? t("communication:match.yes") : t("communication:match.no");

  const colourClass = match
    ? "bg-blue-50 text-blue-900 dark:bg-blue-900/20 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
    : "bg-muted text-muted-foreground border border-border";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${colourClass}`}
      aria-label={`${t("communication:columns.match")}: ${label}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helper: Source link
// ---------------------------------------------------------------------------

function SourceLink({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-[44px] items-center gap-1 text-xs text-primary underline-offset-2 hover:underline"
      aria-label={label}
    >
      <ExternalLink className="h-3 w-3 flex-shrink-0 rtl:scale-x-[-1]" aria-hidden="true" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Helper: Table row
// ---------------------------------------------------------------------------

function PatternRow({
  item,
  t,
}: {
  item: CommunicationPatternItem;
  t: TranslateFn;
}) {
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors duration-150">
      {/* Pattern label */}
      <td className="px-4 py-3 align-top text-sm font-medium text-foreground text-start">
        {item.patternLabel}
      </td>

      {/* COVID-era example */}
      <td className="px-4 py-3 align-top text-sm text-muted-foreground text-start">
        <div className="flex items-start gap-1">
          <span className="flex-1">{item.covidExample}</span>
          <div className="mt-0.5 flex-shrink-0">
            <SourceLink
              url={item.covidSourceUrl}
              label={`${t("communication:source")}: ${item.covidSourceUrl}`}
            />
          </div>
        </div>
      </td>

      {/* Current hantavirus example */}
      <td className="px-4 py-3 align-top text-sm text-muted-foreground text-start">
        <div className="flex items-start gap-1">
          <span className="flex-1">{item.currentExample}</span>
          <div className="mt-0.5 flex-shrink-0">
            <SourceLink
              url={item.currentSourceUrl}
              label={`${t("communication:source")}: ${item.currentSourceUrl}`}
            />
          </div>
        </div>
      </td>

      {/* Match */}
      <td className="px-4 py-3 align-top text-center">
        <MatchBadge match={item.match} t={t} />
      </td>

      {/* Severity */}
      <td className="px-4 py-3 align-top text-center">
        <SeverityBadge severity={item.severity} t={t} />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CommunicationPatternsTable({ locale, t }: CommunicationPatternsTableProps) {
  const { data, isLoading, isError } = useCommunicationPatterns({ locale });

  return (
    <section
      id="communication-patterns"
      aria-labelledby="comm-patterns-heading"
      className="space-y-4"
    >
      {/* Non-dismissible analytical framing banner */}
      <div
        role="note"
        aria-live="polite"
        className="rounded-lg border border-border bg-muted px-4 py-3 text-sm text-muted-foreground"
      >
        <span className="font-medium">{t("communication:banner.disclaimer")}</span>
      </div>

      <h2 id="comm-patterns-heading" className="text-xl font-semibold text-foreground">
        {t("communication:columns.pattern")}
      </h2>

      {/* Loading state */}
      {isLoading && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("communication:loading")}
        </p>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <p className="text-sm text-destructive" role="alert" aria-live="assertive">
          {t("communication:error")}
        </p>
      )}

      {/* Empty state */}
      {!isLoading && !isError && data?.length === 0 && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("communication:empty")}
        </p>
      )}

      {/* Table */}
      {!isLoading && !isError && data && data.length > 0 && (
        <div className="grid gap-3 md:hidden">
          {data.map((item) => (
            <article
              key={item.patternId}
              className="rounded-lg border border-border bg-card p-4 shadow-sm"
            >
              <h3 className="text-base font-semibold text-foreground">{item.patternLabel}</h3>
              <dl className="mt-3 grid gap-3 text-sm">
                <div>
                  <dt className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("communication:columns.covid")}
                  </dt>
                  <dd className="mt-1 flex items-start gap-2 text-muted-foreground">
                    <span className="min-w-0 flex-1">{item.covidExample}</span>
                    <SourceLink
                      url={item.covidSourceUrl}
                      label={`${t("communication:source")}: ${item.covidSourceUrl}`}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("communication:columns.current")}
                  </dt>
                  <dd className="mt-1 flex items-start gap-2 text-muted-foreground">
                    <span className="min-w-0 flex-1">{item.currentExample}</span>
                    <SourceLink
                      url={item.currentSourceUrl}
                      label={`${t("communication:source")}: ${item.currentSourceUrl}`}
                    />
                  </dd>
                </div>
                <div className="flex flex-wrap gap-2">
                  <MatchBadge match={item.match} t={t} />
                  <SeverityBadge severity={item.severity} t={t} />
                </div>
              </dl>
            </article>
          ))}
        </div>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <div className="hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
          <table
            className="min-w-full divide-y divide-border"
            aria-label={t("communication:columns.pattern")}
          >
            <thead className="bg-muted/50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  {t("communication:columns.pattern")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  {t("communication:columns.covid")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  {t("communication:columns.current")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  {t("communication:columns.match")}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  {t("communication:columns.severity")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {data.map((item) => (
                <PatternRow key={item.patternId} item={item} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
